/**
 * Theme colors from env (VITE_THEME_*).
 * Values are HSL triplets without "hsl()", e.g. "217 91% 34%".
 * When set, we inject CSS variables so the app uses dark/light blue (or any palette).
 * Uses direct import.meta.env.VITE_* so Vite replaces them at build time.
 */

/** HSL values for light mode (optional). When set, overrides default palette. */
const light = {
  primary: import.meta.env.VITE_THEME_PRIMARY,
  primaryForeground: import.meta.env.VITE_THEME_PRIMARY_FG,
  background: import.meta.env.VITE_THEME_BACKGROUND,
  foreground: import.meta.env.VITE_THEME_FOREGROUND,
  accent: import.meta.env.VITE_THEME_ACCENT,
  accentForeground: import.meta.env.VITE_THEME_ACCENT_FG,
  secondary: import.meta.env.VITE_THEME_SECONDARY,
  muted: import.meta.env.VITE_THEME_MUTED,
  mutedForeground: import.meta.env.VITE_THEME_MUTED_FG,
  border: import.meta.env.VITE_THEME_BORDER,
  ring: import.meta.env.VITE_THEME_RING,
  sidebarBackground: import.meta.env.VITE_THEME_SIDEBAR_BG,
  sidebarForeground: import.meta.env.VITE_THEME_SIDEBAR_FG,
};

/** HSL values for .dark mode (optional). */
const dark = {
  primary: import.meta.env.VITE_THEME_DARK_PRIMARY,
  primaryForeground: import.meta.env.VITE_THEME_DARK_PRIMARY_FG,
  background: import.meta.env.VITE_THEME_DARK_BACKGROUND,
  foreground: import.meta.env.VITE_THEME_DARK_FOREGROUND,
  accent: import.meta.env.VITE_THEME_DARK_ACCENT,
  accentForeground: import.meta.env.VITE_THEME_DARK_ACCENT_FG,
  secondary: import.meta.env.VITE_THEME_DARK_SECONDARY,
  muted: import.meta.env.VITE_THEME_DARK_MUTED,
  mutedForeground: import.meta.env.VITE_THEME_DARK_MUTED_FG,
  border: import.meta.env.VITE_THEME_DARK_BORDER,
  ring: import.meta.env.VITE_THEME_DARK_RING,
  sidebarBackground: import.meta.env.VITE_THEME_DARK_SIDEBAR_BG,
  sidebarForeground: import.meta.env.VITE_THEME_DARK_SIDEBAR_FG,
};

function hasAnyTheme(): boolean {
  return Object.values(light).some(Boolean) || Object.values(dark).some(Boolean);
}

/** Build a CSS string that overrides :root and .dark with theme vars. */
function buildThemeCSS(): string {
  if (!hasAnyTheme()) return '';

  const rootVars: string[] = [];
  const darkVars: string[] = [];

  if (light.primary) rootVars.push('--primary: ' + light.primary);
  if (light.primaryForeground) rootVars.push('--primary-foreground: ' + light.primaryForeground);
  if (light.background) rootVars.push('--background: ' + light.background);
  if (light.foreground) rootVars.push('--foreground: ' + light.foreground);
  if (light.accent) rootVars.push('--accent: ' + light.accent);
  if (light.accentForeground) rootVars.push('--accent-foreground: ' + light.accentForeground);
  if (light.secondary) rootVars.push('--secondary: ' + light.secondary);
  if (light.muted) rootVars.push('--muted: ' + light.muted);
  if (light.mutedForeground) rootVars.push('--muted-foreground: ' + light.mutedForeground);
  if (light.border) rootVars.push('--border: ' + light.border);
  if (light.ring) rootVars.push('--ring: ' + light.ring);
  if (light.sidebarBackground) rootVars.push('--sidebar-background: ' + light.sidebarBackground);
  if (light.sidebarForeground) rootVars.push('--sidebar-foreground: ' + light.sidebarForeground);
  if (light.primary) rootVars.push('--sidebar-primary: ' + light.primary);
  if (light.accent) rootVars.push('--sidebar-accent: ' + light.accent);

  if (dark.primary) darkVars.push('--primary: ' + dark.primary);
  if (dark.primaryForeground) darkVars.push('--primary-foreground: ' + dark.primaryForeground);
  if (dark.background) darkVars.push('--background: ' + dark.background);
  if (dark.foreground) darkVars.push('--foreground: ' + dark.foreground);
  if (dark.accent) darkVars.push('--accent: ' + dark.accent);
  if (dark.accentForeground) darkVars.push('--accent-foreground: ' + dark.accentForeground);
  if (dark.secondary) darkVars.push('--secondary: ' + dark.secondary);
  if (dark.muted) darkVars.push('--muted: ' + dark.muted);
  if (dark.mutedForeground) darkVars.push('--muted-foreground: ' + dark.mutedForeground);
  if (dark.border) darkVars.push('--border: ' + dark.border);
  if (dark.ring) darkVars.push('--ring: ' + dark.ring);
  if (dark.sidebarBackground) darkVars.push('--sidebar-background: ' + dark.sidebarBackground);
  if (dark.sidebarForeground) darkVars.push('--sidebar-foreground: ' + dark.sidebarForeground);
  if (dark.primary) darkVars.push('--sidebar-primary: ' + dark.primary);
  if (dark.accent) darkVars.push('--sidebar-accent: ' + dark.accent);

  // Visagio palette: map theme primary/accent so components using visagio-teal etc. get the new colors
  if (light.primary) rootVars.push('--visagio-teal: ' + light.primary);
  if (light.sidebarBackground) rootVars.push('--visagio-teal-dark: ' + light.sidebarBackground);
  if (light.accent) rootVars.push('--visagio-aqua: ' + light.accent);
  if (light.secondary) rootVars.push('--visagio-mint: ' + light.secondary);
  if (light.background) rootVars.push('--visagio-off-white: ' + light.background);
  if (light.foreground) rootVars.push('--visagio-dark: ' + light.foreground);
  if (dark.primary) darkVars.push('--visagio-teal: ' + dark.primary);
  if (dark.sidebarBackground) darkVars.push('--visagio-teal-dark: ' + dark.sidebarBackground);
  if (dark.accent) darkVars.push('--visagio-aqua: ' + dark.accent);
  if (dark.secondary) darkVars.push('--visagio-mint: ' + dark.secondary);
  if (dark.background) darkVars.push('--visagio-off-white: ' + dark.background);
  if (dark.foreground) darkVars.push('--visagio-dark: ' + dark.foreground);

  let css = '';
  if (rootVars.length) css += `:root { ${rootVars.join('; ')} }\n`;
  if (darkVars.length) css += `.dark { ${darkVars.join('; ')} }\n`;
  return css;
}

/** Inject theme overrides into the document if any VITE_THEME_* are set. */
export function injectTheme(): void {
  const css = buildThemeCSS();
  if (!css) return;
  const style = document.createElement('style');
  style.id = 'theme-overrides';
  style.textContent = css;
  document.head.appendChild(style);
}
